import SessionClock from '../SessionClock.jsx';
import SessionProfile from '../SessionProfile.jsx';
import SignalCard from '../SignalCard.jsx';
import OTECalculator from '../OTECalculator.jsx';
import InfoButton from '../../common/InfoButton.jsx';
import ModuleContainer from '../../layout/ModuleContainer.jsx';
import { useState, useEffect } from 'react';
import { ShieldAlert, Target, Ban, Moon } from 'lucide-react';

// ─── Trade Directive Banner ───────────────────────────────────────────────────

function TradeDirective({ instrument }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    async function fetch_() {
      try {
        const [sigRes, sesRes] = await Promise.all([
          fetch(`/api/futures-signals/active?instrument=${instrument}`),
          fetch(`/api/futures-signals/session?instrument=${instrument}`),
        ]);
        const signals = sigRes.ok ? await sigRes.json() : [];
        const session = sesRes.ok ? await sesRes.json() : {};
        setData({ signal: signals[0] || null, session: session.session, currentSession: session.currentSession });
      } catch { /* silent */ }
    }
    fetch_();
    const interval = setInterval(fetch_, 15000);
    return () => clearInterval(interval);
  }, [instrument]);

  if (!data) return null;

  const { signal, session, currentSession } = data;
  const isClosed = !currentSession?.isActive || currentSession?.key === 'CLOSED';
  const profile = session?.ny_profile;
  const noTradeProfile = profile === 'CONSOLIDATION' || profile === 'SEEK_AND_DESTROY';

  // Check for high-impact news blocking
  const newsBlocked = false; // TODO: wire to news severity check

  let state, color, bgColor, Icon, title, subtitle;

  if (isClosed) {
    state = 'CLOSED'; color = 'text-korinth-muted'; bgColor = 'bg-korinth-muted/10 border-korinth-muted/30';
    Icon = Moon; title = 'MARKET CLOSED'; subtitle = `Next session opens ${currentSession?.minutesRemaining ? 'in ' + currentSession.minutesRemaining + ' min' : 'tomorrow'}`;
  } else if (signal) {
    state = 'SIGNAL'; color = 'text-korinth-accent'; bgColor = 'bg-korinth-accent/10 border-korinth-accent/30';
    Icon = Target; title = `TRADE SIGNAL ACTIVE — ${signal.direction} ${instrument} @ ${signal.entry_price?.toFixed(2)}`;
    subtitle = `SL ${signal.stop_loss?.toFixed(2)} | TP ${signal.take_profit_1?.toFixed(2)} | Score ${signal.score}/10`;
  } else if (newsBlocked) {
    state = 'BLOCKED'; color = 'text-korinth-danger'; bgColor = 'bg-korinth-danger/10 border-korinth-danger/30';
    Icon = Ban; title = 'DO NOT TRADE — High-impact news active'; subtitle = 'Wait for news to clear before entering positions';
  } else if (noTradeProfile) {
    state = 'NO_TRADE'; color = 'text-korinth-danger'; bgColor = 'bg-korinth-danger/10 border-korinth-danger/30';
    Icon = ShieldAlert; title = `NO TRADE — ${profile === 'CONSOLIDATION' ? 'Consolidation' : 'Seek & Destroy'} profile`;
    subtitle = profile === 'CONSOLIDATION' ? 'London range too small or choppy — no clear direction. Wait for next session.' : 'Both London H and L swept — market hunting stops on both sides. Stay flat.';
  } else {
    state = 'MONITORING'; color = 'text-korinth-warning'; bgColor = 'bg-korinth-warning/10 border-korinth-warning/30';
    Icon = Target; title = 'MONITORING — Conditions building'; subtitle = session?.locked ? 'Watching for OTE entry in the golden zone' : 'London session tracking — levels not yet locked';
  }

  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${bgColor}`}>
      <Icon className={`w-6 h-6 ${color} flex-shrink-0`} />
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-mono font-bold ${color}`}>{title}</div>
        <div className="text-[10px] text-korinth-muted">{subtitle}</div>
      </div>
    </div>
  );
}

// ─── Quick Stats ──────────────────────────────────────────────────────────────

function QuickStats() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    async function fetch_() {
      try {
        const [sigRes, readRes] = await Promise.all([
          fetch('/api/futures-signals/history?limit=50'),
          fetch('/api/futures-signals/readiness'),
        ]);
        const signals = sigRes.ok ? await sigRes.json() : [];
        const readiness = readRes.ok ? await readRes.json() : {};
        const today = new Date().toISOString().slice(0, 10);
        const todaySignals = signals.filter(s => s.created_at?.startsWith(today));
        const wins = signals.filter(s => s.status?.includes('TP'));
        const total = signals.filter(s => s.status !== 'ACTIVE' && s.status !== 'EXPIRED');
        setStats({
          todayCount: todaySignals.length,
          winRate: total.length > 0 ? Math.round((wins.length / total.length) * 100) : 0,
          totalSignals: signals.length,
          readiness: readiness.score || 0,
        });
      } catch { /* silent */ }
    }
    fetch_();
  }, []);

  if (!stats) return null;

  return (
    <div className="grid grid-cols-4 gap-3">
      {[
        { label: "Today's Signals", value: stats.todayCount, color: 'text-korinth-accent' },
        { label: 'Win Rate', value: stats.winRate + '%', color: stats.winRate >= 55 ? 'text-korinth-accent' : 'text-korinth-warning' },
        { label: 'Total Signals', value: stats.totalSignals, color: 'text-korinth-text' },
        { label: 'Readiness', value: stats.readiness + '/100', color: stats.readiness >= 70 ? 'text-korinth-accent' : 'text-korinth-warning' },
      ].map(s => (
        <div key={s.label} className="bg-korinth-bg rounded-lg border border-korinth-border/50 px-3 py-2 text-center">
          <div className={`text-lg font-mono font-bold ${s.color}`}>{s.value}</div>
          <div className="text-[9px] text-korinth-muted">{s.label}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Recent Sessions ──────────────────────────────────────────────────────────

function RecentSessions({ instrument }) {
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    async function fetchSessions() {
      try {
        const res = await fetch(`/api/futures-signals/history?instrument=${instrument}&limit=5`);
        if (res.ok) {
          const data = await res.json();
          const seen = new Set();
          const unique = data.filter(s => { const key = s.created_at?.slice(0, 10); if (seen.has(key)) return false; seen.add(key); return true; });
          setSessions(unique.slice(0, 5));
        }
      } catch { /* silent */ }
    }
    fetchSessions();
  }, [instrument]);

  if (sessions.length === 0) return null;

  return (
    <ModuleContainer title="Recent Sessions" subtitle="Last 5 session outcomes">
      <div className="overflow-x-auto">
        <table className="w-full text-[10px] font-mono">
          <thead><tr className="border-b border-korinth-border text-korinth-muted">
            <th className="text-left py-1.5 px-2">Date</th><th className="text-left py-1.5 px-2">Profile</th>
            <th className="text-left py-1.5 px-2">London H</th><th className="text-left py-1.5 px-2">London L</th>
            <th className="text-left py-1.5 px-2">Direction</th><th className="text-left py-1.5 px-2">Score</th>
            <th className="text-left py-1.5 px-2">Outcome</th>
          </tr></thead>
          <tbody>
            {sessions.map((s, i) => (
              <tr key={i} className="border-b border-korinth-border/30">
                <td className="py-1.5 px-2 text-korinth-text">{s.created_at?.slice(5, 10)}</td>
                <td className="py-1.5 px-2 text-korinth-text">{s.ny_profile || '—'}</td>
                <td className="py-1.5 px-2 text-korinth-accent">{s.london_high?.toFixed(2) || '—'}</td>
                <td className="py-1.5 px-2 text-korinth-danger">{s.london_low?.toFixed(2) || '—'}</td>
                <td className={`py-1.5 px-2 ${s.direction === 'LONG' ? 'text-korinth-accent' : s.direction === 'SHORT' ? 'text-korinth-danger' : 'text-korinth-muted'}`}>{s.direction || '—'}</td>
                <td className="py-1.5 px-2 text-korinth-text">{s.score != null ? s.score + '/10' : '—'}</td>
                <td className={`py-1.5 px-2 font-bold ${s.status?.includes('TP') ? 'text-korinth-accent' : s.status === 'STOPPED' ? 'text-korinth-danger' : 'text-korinth-muted'}`}>{s.status || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ModuleContainer>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function DashboardTab({ instrument, onShadowTrade }) {
  return (
    <div className="space-y-6">
      <TradeDirective instrument={instrument} />
      <QuickStats />
      <SessionClock />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SessionProfile instrument={instrument} />
        <SignalCard instrument={instrument} onShadowTrade={onShadowTrade} />
      </div>
      <OTECalculator instrument={instrument} />
      <RecentSessions instrument={instrument} />
    </div>
  );
}
